import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField, IconButton,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Group, Add, ContentCopy, CheckCircle, Refresh, PersonRemove,
  Delete, ExitToApp, People, ArrowBack, Warning, Sensors, RemoveCircle, Edit as EditIcon,
} from '@mui/icons-material';
import { FormControlLabel, Switch, Autocomplete } from '@mui/material';
import { groupService, stationService } from '../services/api';

const GroupsTab: React.FC = () => {
  const [ownedGroups, setOwnedGroups] = useState<any[]>([]);
  const [memberships, setMemberships] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [newGroupMembersCanAdd, setNewGroupMembersCanAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [copiedToken, setCopiedToken] = useState<number | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<{ groupId: number; userId: number; name: string } | null>(null);
  const [newLinkAfterRemove, setNewLinkAfterRemove] = useState<string | null>(null);
  const [deleteGroupConfirm, setDeleteGroupConfirm] = useState<{ groupId: number; name: string } | null>(null);
  // Station management
  const [addStationOpen, setAddStationOpen] = useState(false);
  const [myStations, setMyStations] = useState<any[]>([]);
  const [selectedStationUuid, setSelectedStationUuid] = useState<string | null>(null);
  const [addingStation, setAddingStation] = useState(false);
  const [removeStationConfirm, setRemoveStationConfirm] = useState<{ groupId: number; stationId: number; stationName: string } | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [owned, member] = await Promise.all([
        groupService.listGroups(),
        groupService.myMemberships(),
      ]);
      setOwnedGroups(owned);
      setMemberships(member);
    } catch (err) {
      console.error('Groups fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!newGroupName.trim()) return;
    setCreating(true);
    try {
      await groupService.createGroup({
        name: newGroupName.trim(),
        description: newGroupDesc.trim(),
        members_can_add_stations: newGroupMembersCanAdd,
      });
      setNewGroupName('');
      setNewGroupDesc('');
      setNewGroupMembersCanAdd(false);
      setCreateOpen(false);
      await fetchData();
    } catch (err) {
      console.error('Create group failed:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = (group: any) => {
    const url = `${window.location.origin}/join/${group.invite_token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(group.id);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleRegenerateToken = async (groupId: number) => {
    try {
      await groupService.regenerateToken(groupId);
      await fetchData();
      if (selectedGroup?.id === groupId) {
        const updated = await groupService.getGroup(groupId);
        setSelectedGroup(updated);
      }
    } catch (err) {
      console.error('Regenerate failed:', err);
    }
  };

  const handleRemoveMember = async (groupId: number, userId: number) => {
    try {
      const result = await groupService.removeMember(groupId, userId);
      const newUrl = `${window.location.origin}/join/${result.new_invite_token}`;
      setNewLinkAfterRemove(newUrl);
      setRemoveConfirm(null);
      const updated = await groupService.getGroup(groupId);
      setSelectedGroup(updated);
      await fetchData();
    } catch (err) {
      console.error('Remove member failed:', err);
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    try {
      await groupService.deleteGroup(groupId);
      setSelectedGroup(null);
      await fetchData();
    } catch (err) {
      console.error('Delete group failed:', err);
    }
  };

  const handleRenameGroup = async () => {
    if (!renameValue.trim() || !selectedGroup) return;
    setRenaming(true);
    try {
      await groupService.renameGroup(selectedGroup.id, renameValue.trim());
      setRenameOpen(false);
      const updated = await groupService.getGroup(selectedGroup.id);
      setSelectedGroup(updated);
      await fetchData();
    } catch (err) {
      console.error('Rename group failed:', err);
    } finally {
      setRenaming(false);
    }
  };

  const handleLeave = async (groupId: number) => {
    try {
      await groupService.leaveGroup(groupId);
      await fetchData();
    } catch (err) {
      console.error('Leave group failed:', err);
    }
  };

  const openGroupDetail = async (groupId: number) => {
    try {
      const detail = await groupService.getGroup(groupId);
      setSelectedGroup(detail);
    } catch (err) {
      console.error('Failed to load group detail:', err);
    }
  };

  const handleOpenAddStation = async () => {
    try {
      const result = await stationService.getAll({ include_public: false });
      setMyStations(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('Failed to load stations:', err);
    }
    setSelectedStationUuid(null);
    setAddStationOpen(true);
  };

  const handleAddStation = async () => {
    if (!selectedStationUuid || !selectedGroup) return;
    setAddingStation(true);
    try {
      await groupService.addStationToGroup(selectedGroup.id, selectedStationUuid);
      setAddStationOpen(false);
      setSelectedStationUuid(null);
      const updated = await groupService.getGroup(selectedGroup.id);
      setSelectedGroup(updated);
      await fetchData();
    } catch (err: any) {
      console.error('Add station failed:', err);
      alert(err.response?.data?.error || 'Failed to add station');
    } finally {
      setAddingStation(false);
    }
  };

  const handleRemoveStation = async (groupId: number, stationId: number) => {
    try {
      await groupService.removeStationFromGroup(groupId, stationId);
      setRemoveStationConfirm(null);
      const updated = await groupService.getGroup(groupId);
      setSelectedGroup(updated);
      await fetchData();
    } catch (err) {
      console.error('Remove station failed:', err);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#00d4ff' }} /></Box>;
  }

  // ═══ Render ═══
  return (
    <>
    {/* Group Detail View */}
    {selectedGroup ? (
      <Box>
        {/* Back button + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <IconButton onClick={() => setSelectedGroup(null)} sx={{ color: '#00d4ff' }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
            {selectedGroup.name}
          </Typography>
          {!selectedGroup.is_owner && (
            <Chip label="Member" size="small" sx={{ background: 'rgba(76,175,80,0.15)', color: '#4caf50' }} />
          )}
          {selectedGroup.is_owner && selectedGroup.members && (
            <Chip
              icon={<People sx={{ fontSize: 16 }} />}
              label={`${selectedGroup.members.length} members`}
              sx={{ background: 'rgba(0,212,255,0.15)', color: '#00d4ff' }}
            />
          )}
          <Chip
            icon={<Sensors sx={{ fontSize: 16 }} />}
            label={`${selectedGroup.stations?.length || 0} stations`}
            sx={{ background: 'rgba(76,175,80,0.15)', color: '#4caf50' }}
          />
        </Box>
        {selectedGroup.description && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2, ml: 6 }}>
            {selectedGroup.description}
          </Typography>
        )}

        {/* Invite Link — owner only */}
        {selectedGroup.is_owner && (
        <Card sx={{ mb: 3, background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#00d4ff' }}>
              Invite Link
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, background: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', flex: 1, wordBreak: 'break-all' }}>
                {window.location.origin}/join/{selectedGroup.invite_token}
              </Typography>
              <Button
                size="small"
                startIcon={copiedToken === selectedGroup.id ? <CheckCircle /> : <ContentCopy />}
                onClick={() => handleCopy(selectedGroup)}
                sx={{ color: copiedToken === selectedGroup.id ? '#4caf50' : '#00d4ff', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
              >
                {copiedToken === selectedGroup.id ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                size="small"
                startIcon={<Refresh />}
                onClick={() => handleRegenerateToken(selectedGroup.id)}
                sx={{ color: '#ff9800', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
              >
                Regenerate
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', mt: 1, display: 'block' }}>
              Share this link to invite users. Regenerating creates a new link and invalidates the old one.
            </Typography>
          </CardContent>
        </Card>
        )}

        {/* Members List — owner only */}
        <Card sx={{ mb: 3, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'rgba(255,255,255,0.7)' }}>
              Members
            </Typography>
            {selectedGroup.members?.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', textAlign: 'center', py: 3 }}>
                {selectedGroup.is_owner ? 'No members yet. Share the invite link to add people.' : 'No other members yet.'}
              </Typography>
            ) : (
              selectedGroup.members?.map((m: any) => (
                <Box key={m.id} sx={{
                  display: 'flex', alignItems: 'center', gap: 2, p: 1.5, mb: 1,
                  background: 'rgba(0,0,0,0.15)', borderRadius: 1,
                }}>
                  {/* Avatar circle */}
                  <Box sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 700, fontSize: '0.9rem',
                  }}>
                    {(m.name || '?')[0].toUpperCase()}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {m.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                      Joined {new Date(m.joined_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </Typography>
                  </Box>
                  {selectedGroup.is_owner && (
                  <IconButton onClick={() => setRemoveConfirm({ groupId: selectedGroup.id, userId: m.user_id, name: m.name })} sx={{ color: '#f44336' }}>
                    <PersonRemove />
                  </IconButton>
                  )}
                </Box>
              ))
            )}
          </CardContent>
        </Card>

        {/* Stations */}
        <Card sx={{ mb: 3, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Sensors sx={{ fontSize: 18 }} />
                Shared Stations
              </Typography>
              {/* Show Add Station: for owner (if has members), for member (if members_can_add_stations) */}
              {(selectedGroup.is_owner || selectedGroup.members_can_add_stations) && (
                <Button size="small" startIcon={<Add />} onClick={handleOpenAddStation}
                  disabled={selectedGroup.is_owner && selectedGroup.members?.length === 0}
                  sx={{ color: '#00d4ff', fontSize: '0.85rem', '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}
                >
                  Add Station
                </Button>
              )}
            </Box>
            {selectedGroup.is_owner && selectedGroup.members?.length === 0 && selectedGroup.stations?.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'rgba(255,152,0,0.7)', textAlign: 'center', py: 3 }}>
                Add at least one member to the group before sharing stations.
              </Typography>
            ) : selectedGroup.stations?.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', textAlign: 'center', py: 3 }}>
                {selectedGroup.is_owner
                  ? 'No stations shared yet. Add stations for group members to view.'
                  : 'No stations shared in this group yet.'}
              </Typography>
            ) : (
              selectedGroup.stations?.map((s: any) => (
                <Box key={s.id} sx={{
                  display: 'flex', alignItems: 'center', gap: 2, p: 1.5, mb: 1,
                  background: 'rgba(0,0,0,0.15)', borderRadius: 1,
                }}>
                  <Sensors sx={{ color: '#4caf50', fontSize: 24 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {s.station_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                      {s.station_source ? `Source: ${s.station_source} · ` : ''}
                      Added by {s.added_by} · {new Date(s.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </Typography>
                  </Box>
                  {/* Remove: owner or the person who added it */}
                  {(selectedGroup.is_owner || s.added_by_id === selectedGroup.current_user_id) && (
                  <IconButton
                    onClick={() => setRemoveStationConfirm({ groupId: selectedGroup.id, stationId: s.station_id, stationName: s.station_name })}
                    sx={{ color: '#f44336' }}
                  >
                    <RemoveCircle />
                  </IconButton>
                  )}
                </Box>
              ))
            )}
            {selectedGroup.is_owner && selectedGroup.members_can_add_stations && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', display: 'block', mt: 1 }}>
                Group members can also add their own stations.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Group Management — owner only */}
        {selectedGroup.is_owner && (
        <>
        <Card sx={{ mb: 2, background: 'rgba(0,212,255,0.03)', border: '1px solid rgba(0,212,255,0.12)' }}>
          <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#00d4ff', fontWeight: 700 }}>Rename Group</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                Change the display name of this group.
              </Typography>
            </Box>
            <Button
              variant="outlined" startIcon={<EditIcon />}
              onClick={() => { setRenameValue(selectedGroup.name); setRenameOpen(true); }}
              sx={{ borderColor: '#00d4ff', color: '#00d4ff', '&:hover': { borderColor: '#33dcff', backgroundColor: 'rgba(0,212,255,0.08)' } }}
            >
              Rename
            </Button>
          </CardContent>
        </Card>
        <Card sx={{ background: 'rgba(244,67,54,0.05)', border: '1px solid rgba(244,67,54,0.15)' }}>
          <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#f44336', fontWeight: 700 }}>Delete Group</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                Permanently delete this group and remove all members.
              </Typography>
            </Box>
            <Button
              variant="outlined" color="error" startIcon={<Delete />}
              onClick={() => setDeleteGroupConfirm({ groupId: selectedGroup.id, name: selectedGroup.name })}
            >
              Delete
            </Button>
          </CardContent>
        </Card>
        </>
        )}
      </Box>
    ) : (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Group sx={{ color: '#00d4ff', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Groups</Typography>
        </Box>
        <Button
          variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}
          sx={{ background: 'linear-gradient(135deg, #00d4ff, #0099cc)', fontWeight: 600 }}
        >
          New Group
        </Button>
      </Box>

      {/* My Groups (as owner) */}
      {ownedGroups.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'rgba(255,255,255,0.6)' }}>
            My Groups
          </Typography>
          <Grid container spacing={2}>
            {ownedGroups.map((group) => (
              <Grid item xs={12} sm={6} md={4} key={group.id}>
                <Card sx={{
                  background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)',
                  border: '1px solid rgba(0,212,255,0.15)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { border: '1px solid rgba(0,212,255,0.4)', transform: 'translateY(-2px)' },
                }} onClick={() => openGroupDetail(group.id)}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 1 }}>
                      {group.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <People sx={{ fontSize: 16, color: 'rgba(255,255,255,0.4)' }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          {group.member_count} member{group.member_count !== 1 ? 's' : ''} + owner
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Sensors sx={{ fontSize: 16, color: 'rgba(255,255,255,0.4)' }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          {group.station_count || 0} station{group.station_count !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button size="small" startIcon={copiedToken === group.id ? <CheckCircle /> : <ContentCopy />}
                        onClick={(e) => { e.stopPropagation(); handleCopy(group); }}
                        sx={{ fontSize: '0.85rem', color: copiedToken === group.id ? '#4caf50' : '#00d4ff' }}
                      >
                        {copiedToken === group.id ? 'Copied!' : 'Copy Link'}
                      </Button>
                      <Button size="small" startIcon={<Refresh />}
                        onClick={(e) => { e.stopPropagation(); handleRegenerateToken(group.id); }}
                        sx={{ fontSize: '0.85rem', color: '#ff9800' }}
                      >
                        New Link
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Groups I'm a member of */}
      {memberships.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'rgba(255,255,255,0.6)' }}>
            Groups I belong to
          </Typography>
          <Grid container spacing={2}>
            {memberships.map((m) => (
              <Grid item xs={12} sm={6} md={4} key={m.group_id}>
                <Card sx={{
                  background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)',
                  border: '1px solid rgba(76,175,80,0.15)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { border: '1px solid rgba(76,175,80,0.4)', transform: 'translateY(-2px)' },
                }} onClick={() => openGroupDetail(m.group_id)}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                      {m.group_name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mb: 0.5 }}>
                      Owner: {m.owner_name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                        Joined {new Date(m.joined_at).toLocaleDateString()}
                      </Typography>
                      <Button size="small" startIcon={<ExitToApp />}
                        onClick={(e) => { e.stopPropagation(); handleLeave(m.group_id); }}
                        sx={{ fontSize: '0.85rem', color: '#f44336' }}
                      >
                        Leave
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Empty state */}
      {ownedGroups.length === 0 && memberships.length === 0 && (
        <Card sx={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', textAlign: 'center', py: 6 }}>
          <CardContent>
            <Group sx={{ fontSize: 48, color: 'rgba(255,255,255,0.15)', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.4)', mb: 1 }}>No groups yet</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.25)', mb: 2 }}>
              Create a group to collaborate with your team.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}
              sx={{ background: 'linear-gradient(135deg, #00d4ff, #0099cc)' }}
            >
              Create First Group
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create Group Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create New Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Group Name" value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth label="Description (optional)" value={newGroupDesc}
            onChange={(e) => setNewGroupDesc(e.target.value)}
            multiline rows={2}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={newGroupMembersCanAdd}
                onChange={(e) => setNewGroupMembersCanAdd(e.target.checked)}
                color="primary"
              />
            }
            label="Members can add stations to this group"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating || !newGroupName.trim()} variant="contained">
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    )}

    {/* Delete Group Confirmation */}
      <Dialog open={!!deleteGroupConfirm} onClose={() => setDeleteGroupConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning sx={{ color: '#f44336' }} />
          Delete Group
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete <strong>{deleteGroupConfirm?.name}</strong>?
          </Typography>
          <Card sx={{ background: 'rgba(244,67,54,0.08)', border: '1px solid rgba(244,67,54,0.2)' }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 600 }}>
                This action cannot be undone
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
                All members will lose access and the invite link will be permanently invalidated.
              </Typography>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteGroupConfirm(null)}>Cancel</Button>
          <Button
            onClick={() => { if (deleteGroupConfirm) { handleDeleteGroup(deleteGroupConfirm.groupId); setDeleteGroupConfirm(null); } }}
            variant="contained" color="error" startIcon={<Delete />}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Member Confirmation */}
      <Dialog open={!!removeConfirm} onClose={() => setRemoveConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning sx={{ color: '#ff9800' }} />
          Remove Member
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove <strong>{removeConfirm?.name}</strong> from this group?
          </Typography>
          <Card sx={{ background: 'rgba(255,152,0,0.08)', border: '1px solid rgba(255,152,0,0.2)' }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="body2" sx={{ color: '#ff9800', fontWeight: 600 }}>
                Security notice
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
                For security, the invite link will be automatically regenerated. You will need to share the new link with other users who still need to join.
              </Typography>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveConfirm(null)}>Cancel</Button>
          <Button
            onClick={() => removeConfirm && handleRemoveMember(removeConfirm.groupId, removeConfirm.userId)}
            variant="contained" color="error" startIcon={<PersonRemove />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Station to Group */}
      <Dialog open={addStationOpen} onClose={() => setAddStationOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Sensors sx={{ color: '#00d4ff' }} />
          Add Station to Group
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2 }}>
            Select one of your stations to share with this group. All members will be able to view its data.
          </Typography>
          <Autocomplete
            options={myStations}
            getOptionLabel={(option: any) => option.name + (option.source ? ` (${option.source})` : '')}
            value={myStations.find((s: any) => s.uuid === selectedStationUuid) || null}
            onChange={(_, val) => setSelectedStationUuid(val?.uuid || null)}
            renderInput={(params) => (
              <TextField {...params} label="Search stations" placeholder="Type to search..." />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddStationOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddStation}
            disabled={addingStation || !selectedStationUuid}
            variant="contained"
          >
            {addingStation ? 'Adding...' : 'Add Station'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Station Confirmation */}
      <Dialog open={!!removeStationConfirm} onClose={() => setRemoveStationConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning sx={{ color: '#ff9800' }} />
          Remove Station
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to remove <strong>{removeStationConfirm?.stationName}</strong> from this group?
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1 }}>
            Group members will no longer be able to view this station's data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveStationConfirm(null)}>Cancel</Button>
          <Button
            onClick={() => removeStationConfirm && handleRemoveStation(removeStationConfirm.groupId, removeStationConfirm.stationId)}
            variant="contained" color="error" startIcon={<RemoveCircle />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Group Dialog */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon sx={{ color: '#00d4ff' }} />
          Rename Group
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Group Name" value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRenameGroup()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button onClick={handleRenameGroup} disabled={renaming || !renameValue.trim()} variant="contained">
            {renaming ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Link After Remove */}
      <Dialog open={!!newLinkAfterRemove} onClose={() => setNewLinkAfterRemove(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle sx={{ color: '#4caf50' }} />
          Member Removed
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The member has been removed and the invite link has been regenerated. Share the new link with users who need access:
          </Typography>
          <Box sx={{ p: 1.5, background: 'rgba(0,212,255,0.05)', borderRadius: 1, border: '1px solid rgba(0,212,255,0.2)' }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all', color: 'rgba(255,255,255,0.7)' }}>
              {newLinkAfterRemove}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<ContentCopy />}
            onClick={() => { navigator.clipboard.writeText(newLinkAfterRemove || ''); }}
            sx={{ color: '#00d4ff' }}
          >
            Copy New Link
          </Button>
          <Button onClick={() => setNewLinkAfterRemove(null)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GroupsTab;
